import { makeStyles } from '@material-ui/core/styles';

const styles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.palette.custom.background.area,
    borderRadius: theme.shape.borderRadius,
    transition: 'all .3s ease',

    '&.locked': {
      cursor: 'default',
    },

    '&:not(.locked):hover': {
      boxShadow: theme.shadows[25],
      transition: 'all .3s ease',

      '& .title': {
        color: theme.palette.primary.main,
      },
    },

    '& .img': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.custom.surface[3],
      position: 'relative',
      overflow: 'hidden',
      gridArea: 'img',

      '& .icon': {
        fontSize: theme.spacing(6),
        position: 'absolute',
        color: theme.palette.text.disabled,
      },

      '& img': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
    },

    '& .progress': {
      gridArea: 'progress',
      display: 'flex',
      alignItems: 'center',

      '&-bar': {
        display: 'flex',
        flexGrow: 1,
        height: theme.spacing(1) / 2,
        maxHeight: theme.spacing(1) / 2,
        backgroundColor: theme.palette.custom.surface[2],

        '&-fill': {
          display: 'flex',
          height: '100%',
          backgroundColor: theme.palette.primary.main,
          borderRadius: `0 ${theme.spacing(1) / 2}px ${
            theme.spacing(1) / 2
          }px 0`,
          transition: 'all 1s ease',
        },
      },
      '&-text span': {
        [theme.breakpoints.down('xs')]: {
          display: 'none',
        },
      },
    },

    '& .content': {
      gridArea: 'content',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,

      '& .title': {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,

        [theme.breakpoints.down('xs')]: {
          ...theme.typography.caption,
          maxHeight: `calc(${theme.typography.caption.lineHeight}em * 2)`,
          overflow: 'hidden',
        },
      },

      '& .instructor': {
        color: theme.palette.text.hint,
      },
    },

    '& .lock': {
      gridArea: 'lock',
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.disabled,
    },

    '&.grid': {
      display: 'flex',
      flexDirection: 'column',

      [theme.breakpoints.down('xs')]: {
        width: '100%',
        maxWidth: 300,
        margin: '0 auto',
      },

      '& .img': {
        width: '100%',
        padding: 'calc(100% / 2.8) 0',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
      },

      '& .content': {
        padding: theme.spacing(2),
        display: 'grid',
        gridTemplate: `
          "title title" auto
          "instructor lock" auto / 1fr auto
        `,

        '&.skeleton': {
          gridTemplate: `
            "title title" auto
            "instructor instructor" auto / 1fr auto
          `,
        },

        '& .title': {
          gridArea: 'title',
          marginBottom: theme.spacing(2),
        },

        '& .instructor': {
          gridArea: 'instructor',
          margin: 'auto 0',
        },
      },
    },

    '&.list': {
      display: 'grid',
      '&': {
        gridTemplate: `
          "img content content content" auto
          "img desc desc desc" auto
          "img lock progress footer" auto / minmax(auto, 300px) auto 1fr 1fr
        `,

        [theme.breakpoints.only('sm')]: {
          gridTemplate: `
            "img content content" auto
            "img progress progress" auto
            "desc desc desc" auto
            "footer footer footer" auto / minmax(auto, 240px) 1fr 1fr
          `,
        },

        [theme.breakpoints.only('xs')]: {
          gridTemplate: `
            "img content content" auto
            "progress progress progress" auto
            "desc desc desc" auto
            "footer footer footer" auto / minmax(auto, 40%) 1fr 1fr
          `,
        },
      },

      '& .img': {
        padding: 'calc(100% / 2.8) 0',
        borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,

        [theme.breakpoints.down('sm')]: {
          borderRadius: `${theme.shape.borderRadius}px 0 0 0`,
        },
      },

      '& .progress': {
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),

          '&-bar': {
            maxWidth: 250,
          },
        },
        '&-text': {
          marginLeft: theme.spacing(1),
          color: theme.palette.text.hint,

          [theme.breakpoints.down('xs')]: {
            display: 'none',
          },
        },
      },

      '& .content': {
        padding: theme.spacing(2, 2, 1),

        '& .title': {
          [theme.breakpoints.up('md')]: {
            ...theme.typography.subtitle1,
          },
        },
      },

      '& .description': {
        gridArea: 'desc',
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,

        [theme.breakpoints.down('sm')]: {
          backgroundColor: theme.palette.custom.surface[0],
          '&:not(.active)': {
            display: 'none',
          },
          '&-text': {
            ...theme.typography.caption,
          },
        },
      },

      '& > .lock': {
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },

      '& .footer-bar': {
        gridArea: 'footer',
        minHeight: theme.spacing(6),
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),

        [theme.breakpoints.down('sm')]: {
          backgroundColor: theme.palette.custom.surface[0],
        },

        [theme.breakpoints.up('md')]: {
          '& .btn-info': {
            display: 'none',
          },
          '& .lock': {
            display: 'none',
          },
        },

        '& .btn-info': {
          '&.active': {
            color: theme.palette.primary.main,
          },
        },

        '& .lock': {
          margin: theme.spacing(0, 2),
        },

        '& .btn': {
          marginLeft: 'auto',
          padding: `${theme.spacing(1) / 2}px ${theme.spacing(1)}px`,
          borderRadius: theme.shape.borderRadius,
          color: theme.palette.text.hint,
          fontWeight: theme.typography.fontWeightMedium,
          transition: 'all .3s ease',

          '&:hover': {
            backgroundColor: theme.palette.custom.surface[2],
          },
        },
      },
    },
  },
}));

export default styles;
